import {
	Content as RadixTooltipContent,
	Arrow as TooltipArrow,
	Portal as TooltipPortal,
	// biome-ignore lint/style/noRestrictedImports: component usage
} from "@radix-ui/react-tooltip";
import React from "react";

import { ZIndexBoundary } from "~/components/z-index-boundary";
import { type VariantProp, type VariantProps, cv } from "~/tools/class-variance";

import { useIsTooltipHidden } from "./_context";
import { useIsEnabled } from "./_enabler";
import styles from "./_tooltip.module.css";

const variants = cv(styles.content, {
	variants: {
		width: {
			auto: null,
			"trigger-max": styles.widthMax,
			"trigger-min": styles.widthMin,
			"trigger-same": styles.widthSame,
		},
	},

	defaultVariants: {
		width: "auto",
	},
});

/**
 * The width of the tooltip content.
 * - `auto` (default): The width is determined by the content.
 * - `trigger-max`: The width is at most the width of the trigger element.
 * - `trigger-min`: The width is at least the width of the trigger element.
 * - `trigger-same`: The width matches the width of the trigger element.
 */
export type TooltipContentWidth = VariantProp<typeof variants, "width">;

export interface TooltipContentProps
	extends VariantProps<typeof variants>,
		React.ComponentProps<typeof RadixTooltipContent> {}

export const TooltipContent = React.forwardRef<React.ComponentRef<typeof RadixTooltipContent>, TooltipContentProps>(
	({ className, width, style, children, ...props }, ref) => {
		const hidden = useIsTooltipHidden();
		const isEnabled = useIsEnabled();

		if (hidden || !isEnabled || !children) {
			return null;
		}

		return (
			<TooltipPortal>
				<ZIndexBoundary>
					{(zIndex = 50) => (
						<RadixTooltipContent
							arrowPadding={4}
							className={variants({ width, className })}
							collisionPadding={8} // avoid sticking to the edges of the boundaries
							ref={ref} // avoid rendering the arrow on the rounded corners
							sideOffset={4} // distance from the trigger
							style={{ zIndex: zIndex + 1, ...style }}
							{...props}
						>
							{children}
							<TooltipArrow className={styles.arrow} height={6} width={12} />
						</RadixTooltipContent>
					)}
				</ZIndexBoundary>
			</TooltipPortal>
		);
	},
);

if (import.meta.env.DEV) {
	TooltipContent.displayName = "TooltipContent";
}
